import { Badge, cn } from "@dl/web-core-js/ui";
import { useTranslation } from "react-i18next";
import type { ReplenishmentZone } from "../../../api/replenishment";

/**
 * The pieces every replenishment view shares: how a zone looks, the zone bar
 * that shows where a material sits against its thresholds, and number
 * formatting. One definition, so red means the same thing on every screen.
 */

const CAD = new Intl.NumberFormat(undefined, { style: "currency", currency: "CAD", maximumFractionDigits: 0 });
const CAD_CENTS = new Intl.NumberFormat(undefined, { style: "currency", currency: "CAD", minimumFractionDigits: 2 });
const MONEY = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const PRICE = new Intl.NumberFormat(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 });
const QUANTITY = new Intl.NumberFormat(undefined, { maximumFractionDigits: 2 });
const WHOLE = new Intl.NumberFormat(undefined, { maximumFractionDigits: 0 });

/** Whole dollars, for headers and totals. */
export function formatCad(value: number | null | undefined): string {
  return value === null || value === undefined ? "" : CAD.format(value);
}

/** Dollars and cents, for a line's price. */
export function formatCadCents(value: number | null | undefined): string {
  return value === null || value === undefined ? "" : CAD_CENTS.format(value);
}

/** An amount in an order's own currency, which may not be CAD: grouped, two decimals, no symbol. */
export function formatMoney(value: number | null | undefined): string {
  return value === null || value === undefined ? "" : MONEY.format(Number(value));
}

/** A unit price: two to four decimals, since some run to fractions of a cent. */
export function formatPrice(value: number | null | undefined): string {
  return value === null || value === undefined ? "" : PRICE.format(Number(value));
}

/** A quantity, trimmed to two decimals and grouped. */
export function formatQuantity(value: number | null | undefined): string {
  return value === null || value === undefined ? "" : QUANTITY.format(value);
}

export function formatWhole(value: number | null | undefined): string {
  return value === null || value === undefined ? "" : WHOLE.format(value);
}

/** Days of cover as people say it: "12 d", "3 wk", or "out" below zero. */
export function formatCover(days: number | null | undefined, t: (key: string, fallback: string) => string): string {
  if (days === null || days === undefined) return "";
  if (days < 0) return t("replenishment.cover.out", "out");
  if (days >= 60) return `${WHOLE.format(days / 7)} ${t("replenishment.cover.weeks", "wk")}`;
  return `${WHOLE.format(days)} ${t("replenishment.cover.days", "d")}`;
}

/** Tailwind classes per zone, drawn from the theme's semantic tokens. */
export const ZONE_TONE: Record<ReplenishmentZone | "OUT", { text: string; soft: string; solid: string }> = {
  OUT: { text: "text-risk", soft: "bg-risk-soft", solid: "bg-risk" },
  RED: { text: "text-risk", soft: "bg-risk-soft", solid: "bg-risk" },
  YELLOW: { text: "text-warn", soft: "bg-warn-soft", solid: "bg-warn" },
  GREEN: { text: "text-ok", soft: "bg-ok-soft", solid: "bg-ok" },
  OVER: { text: "text-info", soft: "bg-info-soft", solid: "bg-info" },
};

/** The word for a zone, shared by the badge and the filters that list zones. */
export function zoneLabel(zone: ReplenishmentZone | "OUT", t: (key: string, fallback: string) => string): string {
  return {
    OUT: t("replenishment.zone.out", "Out"),
    RED: t("replenishment.zone.red", "Red"),
    YELLOW: t("replenishment.zone.yellow", "Order"),
    GREEN: t("replenishment.zone.green", "OK"),
    OVER: t("replenishment.zone.over", "Over"),
  }[zone];
}

export function ZoneBadge({ zone }: { zone: ReplenishmentZone | "OUT" }) {
  const { t } = useTranslation();
  const variant = { OUT: "error", RED: "error", YELLOW: "warning", GREEN: "success", OVER: "info" } as const;
  return (
    <Badge variant={variant[zone]} size="sm">
      {zoneLabel(zone, t)}
    </Badge>
  );
}

/**
 * Where a material sits against its zones: red up to the safety stock, yellow
 * up to the order point, green up to the maximum. The marker is the position;
 * a hollow one to its right is where ordering the suggestion would take it.
 * Below zero pins to the left edge, over the maximum to the right — the
 * number beside the bar says how far.
 */
export function ZoneBar({
  red,
  minimum,
  maximum,
  position,
  after,
  className,
}: {
  red: number;
  minimum: number;
  maximum: number;
  position: number;
  /** Position once the quantity being ordered arrives. */
  after?: number | null;
  className?: string;
}) {
  const top = Math.max(maximum * 1.15, position, after ?? 0, 1);
  const pct = (value: number) => `${String(Math.min(100, Math.max(0, (value / top) * 100)))}%`;
  const noZones = maximum <= 0;
  return (
    <div className={cn("relative h-2 w-full min-w-16 overflow-visible rounded-full bg-surface-sunk", className)} aria-hidden>
      {!noZones && (
        <>
          <div className="absolute inset-y-0 left-0 rounded-l-full bg-risk/70" style={{ width: pct(red) }} />
          <div className="absolute inset-y-0 bg-warn/70" style={{ left: pct(red), width: `calc(${pct(minimum)} - ${pct(red)})` }} />
          <div className="absolute inset-y-0 bg-ok/60" style={{ left: pct(minimum), width: `calc(${pct(maximum)} - ${pct(minimum)})` }} />
        </>
      )}
      {after !== undefined && after !== null && after > position && (
        <>
          <div
            className="absolute top-1/2 h-px -translate-y-1/2 bg-ink-faint"
            style={{ left: pct(position), width: `calc(${pct(after)} - ${pct(position)})` }}
          />
          <div
            className="absolute top-1/2 size-2.5 -translate-x-1/2 -translate-y-1/2 rounded-full border-2 border-ink-muted bg-surface"
            style={{ left: pct(after) }}
          />
        </>
      )}
      <div
        className={cn(
          "absolute top-1/2 h-3.5 w-1 -translate-x-1/2 -translate-y-1/2 rounded-full ring-2 ring-surface",
          position < 0 ? "bg-risk" : "bg-ink",
        )}
        style={{ left: pct(position) }}
      />
    </div>
  );
}

/** A small label/value pair for explanations: "Lead time · 21 d · supplier". */
export function Fact({ label, value, hint }: { label: string; value: React.ReactNode; hint?: React.ReactNode }) {
  return (
    <div className="flex min-w-0 flex-col gap-0.5">
      <span className="text-[11px] font-medium uppercase tracking-wide text-ink-faint">{label}</span>
      <span className="text-[14px] font-semibold tabular-nums text-ink">{value}</span>
      {hint ? <span className="text-[11px] text-ink-muted">{hint}</span> : null}
    </div>
  );
}
